// File: frontend/src/pages/admin/WebStoryEditor.js

import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useParams, useNavigate } from 'react-router-dom';
import './Admin.css';

const WebStoryEditor = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const token = localStorage.getItem('token');
  
  // LIVE URL
  const API_URL = 'https://indiajagran-backend.onrender.com';

  const [loading, setLoading] = useState(false);
  const [uploadingImg, setUploadingImg] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);

  // Initial Form State
  const [formData, setFormData] = useState({
    title: '',
    slug: '',
    coverImage: '',
    pages: [{ image: '', heading: '', text: '' }]
  });

  const optimizeCloudinaryUrl = (url) => {
    if (!url || typeof url !== 'string') return '';
    if (url.includes('cloudinary.com') && url.includes('/upload/') && !url.includes('q_auto')) {
        return url.replace('/upload/', '/upload/q_auto,f_auto/');
    }
    return url;
  };

  useEffect(() => {
    const fetchStoryDetails = async () => {
        try {
            const { data } = await axios.get(`${API_URL}/web-stories/admin/all`, {
                 headers: { Authorization: `Bearer ${token}` }
            });
            const story = data.find(s => s._id === id);
            if (story) setFormData(story);
        } catch (error) {
            console.error("Error fetching story");
        }
    };
    if (id) {
        setIsEditMode(true);
        fetchStoryDetails();
    }
    // eslint-disable-next-line
  }, [id]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handlePageChange = (index, field, value) => {
    setFormData(prev => {
        const newPages = [...prev.pages];
        newPages[index][field] = value;
        return { ...prev, pages: newPages };
    });
  };

  const addPage = () => {
    setFormData(prev => ({
      ...prev,
      pages: [...prev.pages, { image: '', heading: '', text: '' }]
    }));
  };

  const removePage = (index) => {
    setFormData(prev => ({
       ...prev,
       pages: prev.pages.filter((_, i) => i !== index)
    }));
  };

  const uploadImage = async (e, index = null) => {
    const file = e.target.files[0];
    if (!file) return;

    const uploadData = new FormData();
    uploadData.append('image', file);

    try {
      setUploadingImg(true);
      const { data } = await axios.post(`${API_URL}/api/articles/upload`, uploadData, {
        headers: { 
            'Content-Type': 'multipart/form-data',
            Authorization: `Bearer ${token}`
        }
      });
      
      const rawUrl = data.filePath || data.imageUrl || data.url || data.secure_url || data.file;
      
      if (!rawUrl) throw new Error("Image uploaded but URL missing");
      
      const optimizedUrl = optimizeCloudinaryUrl(rawUrl);

      if (index !== null) {
        handlePageChange(index, 'image', optimizedUrl);
      } else {
        setFormData(prev => ({ ...prev, coverImage: optimizedUrl }));
      }
      setUploadingImg(false);
    } catch (error) {
      console.error("Upload failed", error);
      setUploadingImg(false);
      alert("❌ Image Upload Failed!");
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.title || !formData.slug) return alert("Title and Slug are required");
    if (!formData.coverImage) return alert("⚠️ Cover Image is missing!");
    
    for (let i = 0; i < formData.pages.length; i++) {
        if (!formData.pages[i].image) return alert(`Slide #${i + 1} is missing an image!`);
    }

    try {
      setLoading(true);
      if (isEditMode) {
        await axios.put(`${API_URL}/web-stories/${id}`, formData, { headers: { Authorization: `Bearer ${token}` }});
        alert('✅ Web Story Updated!');
      } else {
        await axios.post(`${API_URL}/web-stories`, formData, { headers: { Authorization: `Bearer ${token}` }});
        alert('✅ Web Story Created!');
      }
      navigate('/admin/web-stories');
      setLoading(false);
    } catch (error) {
      const msg = error.response?.data?.message || 'Error saving story';
      alert(`❌ Error: ${msg}`);
      setLoading(false);
    }
  };

  return (
    <div className="admin-container">
      <h2>{isEditMode ? 'Edit Web Story ✏️' : 'Create New Web Story ⚡'}</h2>
      
      <form onSubmit={handleSubmit} className="story-form">
        <div className="form-group">
            <label htmlFor="title">Story Title</label>
            {/* ✅ FIX 1: ID Added */}
            <input 
                id="title"
                type="text" 
                name="title" 
                value={formData.title} 
                onChange={handleChange} 
                required 
                placeholder="Ex: Top 10 Places in India" 
            />
        </div>

        <div className="form-group">
            <label htmlFor="slug">Slug (URL)</label>
            {/* ✅ FIX 2: ID Added */}
            <input 
                id="slug"
                type="text" 
                name="slug" 
                value={formData.slug} 
                onChange={handleChange} 
                required 
                disabled={isEditMode} 
                placeholder="ex: top-10-places-india" 
            />
        </div>

        {/* Cover Image Upload */}
        <div className="form-group">
            <label htmlFor="coverImage">
                Cover Image (Portrait 9:16) 
                {uploadingImg && <span style={{color:'blue', fontSize:'0.9rem', marginLeft:'10px'}}> ⏳ Uploading...</span>}
            </label>
            {/* ✅ FIX 3: ID & Name Added */}
            <input 
                id="coverImage"
                name="coverImage"
                type="file" 
                onChange={(e) => uploadImage(e)} 
                accept="image/*" 
            />
            
            {formData.coverImage && <img src={formData.coverImage} alt="Cover" height="150" style={{marginTop:'10px', borderRadius:'10px', border:'2px solid green'}} />}
        </div>

        <hr />
        <h3>Story Slides</h3>

        {formData.pages.map((page, index) => (
          <div key={index} className="slide-box">
            <h4>Slide #{index + 1}</h4>
            
            {/* Slide Image */}
            <div className="form-group">
                <label htmlFor={`slide-image-${index}`}>Slide Image (Vertical):</label>
                {/* ✅ FIX 4: Unique ID & Name Added */}
                <input 
                    id={`slide-image-${index}`}
                    name={`slide-image-${index}`}
                    type="file" 
                    onChange={(e) => uploadImage(e, index)} 
                    accept="image/*" 
                />
                {page.image && (
                    <img src={page.image} alt="Slide" height="100" style={{display: 'block', margin: '10px 0', borderRadius:'5px', border:'2px solid #3b82f6'}} />
                )}
            </div>

            <div className="form-group">
                <label htmlFor={`slide-heading-${index}`}>Headline (Optional)</label>
                {/* ✅ FIX 5: Unique ID & Name Added */}
                <input 
                    id={`slide-heading-${index}`}
                    name={`slide-heading-${index}`}
                    type="text" 
                    placeholder="Slide Headline" 
                    value={page.heading} 
                    onChange={(e) => handlePageChange(index, 'heading', e.target.value)} 
                />
            </div>

            <div className="form-group">
                <label htmlFor={`slide-text-${index}`}>Description (Short)</label>
                {/* ✅ FIX 6: Unique ID & Name Added */}
                <textarea 
                    id={`slide-text-${index}`}
                    name={`slide-text-${index}`}
                    placeholder="Short Text (2-3 lines max)" 
                    value={page.text} 
                    onChange={(e) => handlePageChange(index, 'text', e.target.value)}
                ></textarea>
            </div>

            {formData.pages.length > 1 && (
                <button type="button" onClick={() => removePage(index)}>
                    Remove Slide ❌
                </button>
            )}
          </div>
        ))}

        <button type="button" onClick={addPage}>+ Add New Slide</button>
        
        <button type="submit" disabled={loading || uploadingImg}>
            {loading ? 'Saving...' : uploadingImg ? 'Wait, Image Uploading...' : (isEditMode ? 'Update Story' : 'Publish Story 🚀')}
        </button>

      </form>
    </div>
  );
};

export default WebStoryEditor;